import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';

import { Activity } from 'app/model/pipeline/activity';
import { Owned } from 'app/model/base/named';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { AzureCosmosDbSink } from 'app/model/pipeline/copysink/azure-cosmos-db.copysink';
import { writeBehaviorOptionValues } from 'app/shared/constants/write-behavior.const';
import { CopySinkAdapter } from 'app/model/adapters/activities/copysink/copysink.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureCosmosDbSink),
    asynchronous: true,
    adapts: {
        type: '*',
        writeBatchTimeout: '*',
        nestingSeparator: '*',
        writeBatchSize: '*',
        writeBehavior: '*',
        maxConcurrentConnections: '*',
        structure: '*',
        disableMetricsCollection: '*'
    }
})
export class AzureCosmosDbSinkAdapter extends CopySinkAdapter<AzureCosmosDbSink, Model.DocumentDbCollectionSink> {
    protected override async _toResource(model: AzureCosmosDbSink): Promise<Model.DocumentDbCollectionSink> {
        const result: Model.DocumentDbCollectionSink = {
            type: 'DocumentDbCollectionSink'
        };
        const writeBatchTimeout = ValueAdapter.toResource(model, AzureCosmosDbSink.WRITE_BATCH_TIMEOUT);
        if (writeBatchTimeout != null && writeBatchTimeout !== '') {
            // don't need pass '' to backend
            result.writeBatchTimeout = writeBatchTimeout;
        }
        result.nestingSeparator = ValueAdapter.toResource(model, AzureCosmosDbSink.NESTING_SEPARATOR);
        result.writeBatchSize = ValueAdapter.toResource(model, AzureCosmosDbSink.WRITE_BATCH_SIZE);
        result.writeBehavior = ValueAdapter.toResource(model, AzureCosmosDbSink.WRITE_BEHAVIOR);
        result.disableMetricsCollection = ValueAdapter.toResource(model, AzureCosmosDbSink.DISABLE_METRICS_COLLECTION);
        CopySinkAdapter.maxConcurrentConnectionsToResource(model, result);
        CopySinkAdapter.toCommonCopySinkResource(model, result, this.context);
        return result;
    }

    protected override async _toModel(
        resource: Model.DocumentDbCollectionSink,
        owner: Activity
    ): Promise<AzureCosmosDbSink> {
        const result = Owned.create<AzureCosmosDbSink>(Metamodel.modelFor(AzureCosmosDbSink), owner);
        ValueAdapter.toModel(result, AzureCosmosDbSink.WRITE_BATCH_TIMEOUT, resource.writeBatchTimeout);
        ValueAdapter.toModel(result, AzureCosmosDbSink.NESTING_SEPARATOR, resource.nestingSeparator);
        ValueAdapter.toModel(result, AzureCosmosDbSink.WRITE_BATCH_SIZE, resource.writeBatchSize);
        ValueAdapter.toModel(result, AzureCosmosDbSink.DISABLE_METRICS_COLLECTION, resource.disableMetricsCollection);

        // handle upper case and lower case
        let writeBehavior = resource.writeBehavior;
        if (writeBehavior && typeof writeBehavior === 'string') {
            const lowerCaseValue = writeBehavior.toLowerCase();
            switch (lowerCaseValue) {
                case writeBehaviorOptionValues.insert:
                    writeBehavior = writeBehaviorOptionValues.insert;
                    break;
                case writeBehaviorOptionValues.upsert:
                    writeBehavior = writeBehaviorOptionValues.upsert;
                    break;
                default:
                    break;
            }
        }
        ValueAdapter.toModel(result, AzureCosmosDbSink.WRITE_BEHAVIOR, writeBehavior);

        CopySinkAdapter.maxConcurrentConnectionsToModel(resource, result);
        CopySinkAdapter.toCommonCopySinkModel(result, resource);

        return result;
    }
}
